/**
 * BrokComposer — chat input with Enter-to-send / Shift+Enter newline.
 * [Source: Story P1b spec §8]
 *
 * P3b: Added mic button (Spracheingabe) — PUSH-TO-TALK (press-and-hold).
 * - Hold the mic button (pointer-down) to record; release (pointer-up/leave) to transcribe.
 * - Transcript appends into the textarea — user edits/sends explicitly.
 * - Voice NEVER auto-sends: confirm-before-write is preserved end-to-end.
 * - A too-short tap / empty capture surfaces a friendly hint (not an error).
 * - Mic button is hidden when MediaRecorder is unsupported (no regression for existing tests).
 *
 * Feature B: Added attach (paperclip) button (Dokument anhängen).
 * - Upload-on-select → attachmentId; an "angehängt: <filename>" chip with a remove (x).
 * - attachmentId is passed through onSend and cleared after send.
 * - Extract-to-text is server-side; nothing is auto-sent.
 */
import { useState, useRef, type KeyboardEvent, type FormEvent, type ChangeEvent } from 'react';
import { Send, Mic, MicOff, Loader2, Paperclip } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { useMicRecorder } from '../hooks/useMicRecorder';
import { agentUploadAttachment } from '../../../lib/api-client';
import { AttachmentChip } from './AttachmentChip';

/** MIME types the backend accepts for a chat attachment (mirrors AGENT_ATTACHMENT_ALLOWED_MIME). */
const ATTACHMENT_ACCEPT = 'application/pdf,image/jpeg,image/png,image/tiff';

interface Props {
  onSend: (text: string, attachmentId?: string, attachmentName?: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Injectable for tests; defaults to the real agentUploadAttachment. */
  uploadAttachment?: typeof agentUploadAttachment;
}

export function BrokComposer({ onSend, disabled, placeholder, uploadAttachment }: Props) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const upload = uploadAttachment ?? agentUploadAttachment;

  // Attachment state — one document per turn (cleared after send / on remove).
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [attachmentName, setAttachmentName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  // Transcript appends so dictation can follow typed text; user can still edit before sending
  const mic = useMicRecorder({
    onTranscript: (text) =>
      setValue((prev) => (prev ? `${prev.trim()} ${text}` : text)),
  });

  function clearAttachment() {
    setAttachmentId(null);
    setAttachmentName(null);
    setAttachmentError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setAttachmentError(null);
    try {
      const { attachmentId: id } = await upload(file, { filename: file.name });
      setAttachmentId(id);
      setAttachmentName(file.name);
    } catch (err) {
      setAttachmentError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
      setAttachmentId(null);
      setAttachmentName(null);
    } finally {
      setUploading(false);
      // Reset the input so re-selecting the same file fires change again.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function submit() {
    const text = value.trim();
    if (!text || disabled) return;
    const outAttachmentId = attachmentId ?? undefined;
    // Capture the filename BEFORE clearAttachment nulls it, so the transcript chip
    // (Feature B) can show which document was sent.
    const outAttachmentName = attachmentName ?? undefined;
    setValue('');
    clearAttachment();
    // Preserve the single-arg call shape when there is no attachment (existing tests rely on it).
    if (outAttachmentId) {
      onSend(text, outAttachmentId, outAttachmentName);
    } else {
      onSend(text);
    }
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    submit();
  }

  return (
    <div className="border-t border-border pt-3">
      {/* Attachment chip / error (Feature B) */}
      {attachmentName && (
        <AttachmentChip filename={attachmentName} onRemove={clearAttachment} className="mb-2" />
      )}
      {attachmentError && (
        <div className="mb-2 text-xs text-destructive" role="alert">
          {attachmentError}
        </div>
      )}

      {/* Push-to-talk hint (friendly nudge, not an error) */}
      {mic.hint && (
        <div className="mb-2 text-xs text-muted-foreground" role="status" data-testid="brok-mic-hint">
          {mic.hint}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex items-end gap-2">
      {/* Hidden file input — driven by the paperclip button */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ATTACHMENT_ACCEPT}
        onChange={handleFileChange}
        className="hidden"
        data-testid="brok-attachment-input"
        aria-hidden
        tabIndex={-1}
      />

      {/* Attach (paperclip) button */}
      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled || uploading}
        aria-label="Dokument anhängen"
        title={attachmentError ?? undefined}
      >
        {uploading ? <Loader2 className="animate-spin" /> : <Paperclip />}
      </Button>

      <textarea
        ref={textareaRef}
        rows={1}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        style={{ maxHeight: '120px', overflowY: 'auto' }}
      />

      {/* Mic button — PUSH-TO-TALK: hold to record, release to transcribe.
          Hidden when MediaRecorder unsupported (no regression for existing tests).
          Stays enabled while requesting/recording so pointer-up/leave can fire stop. */}
      {mic.supported && (
        <Button
          type="button"
          size="icon"
          variant={mic.state === 'recording' ? 'destructive' : 'ghost'}
          onPointerDown={(e) => {
            e.preventDefault(); // no focus-steal / text-selection on hold
            mic.start();
          }}
          onPointerUp={mic.stop}
          onPointerLeave={mic.stop}
          onPointerCancel={mic.stop}
          onContextMenu={(e) => e.preventDefault()} // kill long-press context menu
          disabled={disabled || mic.state === 'transcribing'}
          aria-label={
            mic.state === 'recording'
              ? 'Aufnahme stoppen'
              : mic.state === 'denied'
                ? 'Mikrofonzugriff verweigert'
                : 'Spracheingabe starten'
          }
          aria-pressed={mic.state === 'recording'}
          data-mic-state={mic.state}
          title={mic.hint ?? mic.error ?? 'Zum Sprechen gedrückt halten'}
        >
          {mic.state === 'transcribing' ? (
            <Loader2 className="animate-spin" />
          ) : mic.state === 'denied' || mic.state === 'error' ? (
            <MicOff />
          ) : (
            <Mic
              className={mic.state === 'recording' ? 'animate-pulse' : undefined}
            />
          )}
        </Button>
      )}

      <Button
        type="submit"
        size="icon"
        disabled={disabled || !value.trim()}
        aria-label="Senden"
      >
        <Send />
      </Button>
      </form>
    </div>
  );
}
